import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Plus,
  Trash2,
  UserCheck,
  Mail,
  Loader,
  ChevronLeft,
  ChevronRight,
  Edit,
  ToggleLeft,
  ToggleRight,
  Filter,
  X,
} from 'lucide-react';
import CustomSelect from '../components/Common/CustomSelect';
import {
  getAllUsers,
  createUser,
  updateUser,
  updateUserStatus,
  deleteUser,
  UserManagementUser,
} from '../services/userManagementService';
import { toast } from 'react-toastify';
import Modal from '../components/Common/Modal';
import { validate, isRequired, isValidEmail, isValidPassword } from '../utils/validation';
import { MESSAGES } from '../utils/messages';
import { ROLES } from '../constants/roles';

const ITEMS_PER_PAGE = 10;

type FilterState = {
  status: string;
  role: string;
  createdFrom: string;
  createdTo: string;
};

const Users: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<UserManagementUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserManagementUser | null>(null);
  const [userToToggle, setUserToToggle] = useState<UserManagementUser | null>(null);
  const [userToEdit, setUserToEdit] = useState<UserManagementUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    status: '',
    role: '',
    createdFrom: '',
    createdTo: '',
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: ROLES.DOCTOR as string,
    active: 'true',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    role: ROLES.DOCTOR as string,
    active: 'true',
  });
  const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>({});

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const usersList = await getAllUsers();
      setUsers(usersList);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return users.filter((user) => {
      const name = user.name?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';
      const role = user.role?.toLowerCase() || '';
      const matchesSearch = name.includes(q) || email.includes(q) || role.includes(q);

      if (filters.status) {
        const isActive = filters.status === 'active';
        if (user.active !== isActive) return false;
      }

      if (filters.role && user.role !== filters.role) {
        return false;
      }

      if (filters.createdFrom && user.createdAt) {
        const createdDate = new Date(user.createdAt);
        const fromDate = new Date(filters.createdFrom);
        if (createdDate < fromDate) return false;
      }

      if (filters.createdTo && user.createdAt) {
        const createdDate = new Date(user.createdAt);
        const toDate = new Date(filters.createdTo);
        toDate.setHours(23, 59, 59, 999);
        if (createdDate > toDate) return false;
      }

      return matchesSearch;
    });
  }, [users, searchTerm, filters]);

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const hasActiveFilters = Object.values(filters).some((v) => v !== '');

  const clearFilters = () => {
    setFilters({ status: '', role: '', createdFrom: '', createdTo: '' });
    setSearchTerm('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    if (editFormErrors[name]) {
      setEditFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const schema = {
      name: [isRequired('Name')],
      email: [isRequired('Email'), isValidEmail],
      password: [isRequired('Password'), isValidPassword],
    };
    const errors = validate(schema, formData);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateEditForm = (): boolean => {
    const schema = {
      name: [isRequired('Name')],
      email: [isRequired('Email'), isValidEmail],
    };
    const errors = validate(schema, editFormData);
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await createUser({
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        role: formData.role,
        active: formData.active === 'true',
      });
      toast.success(MESSAGES.success.userCreated);
      setIsCreateModalOpen(false);
      setFormData({ name: '', email: '', password: '', role: ROLES.DOCTOR, active: 'true' });
      setFormErrors({});
      fetchUsers();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create user';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEditForm() || !userToEdit) return;

    setIsSubmitting(true);
    try {
      await updateUser(userToEdit.id, {
        name: editFormData.name.trim(),
        email: editFormData.email.trim(),
        role: editFormData.role,
        active: editFormData.active === 'true',
      });
      toast.success(MESSAGES.success.userUpdated);
      setIsEditModalOpen(false);
      setUserToEdit(null);
      setEditFormErrors({});
      fetchUsers();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update user';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!userToToggle) return;

    setIsSubmitting(true);
    try {
      await updateUserStatus(userToToggle.id, !userToToggle.active);
      toast.success(MESSAGES.success.userUpdated);
      setIsStatusModalOpen(false);
      setUserToToggle(null);
      fetchUsers();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update user status';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteUser(userToDelete.id);
      toast.success(MESSAGES.success.userDeleted);
      setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete user';
      toast.error(errorMessage);
    }
  };

  const openCreateModal = () => {
    setFormData({ name: '', email: '', password: '', role: ROLES.DOCTOR as string, active: 'true' });
    setFormErrors({});
    setIsCreateModalOpen(true);
  };

  const openEditModal = (user: UserManagementUser) => {
    setUserToEdit(user);
    setEditFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      active: user.active ? 'true' : 'false',
    });
    setEditFormErrors({});
    setIsEditModalOpen(true);
  };

  const openStatusModal = (user: UserManagementUser) => {
    setUserToToggle(user);
    setIsStatusModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600">Manage system user accounts</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary flex items-center space-x-2 ${hasActiveFilters ? 'ring-2 ring-primary-500' : ''}`}
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full">
                {Object.values(filters).filter((v) => v !== '').length}
              </span>
            )}
          </button>
          <button onClick={openCreateModal} className="btn-primary flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700">Filters</h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
              >
                <X className="h-3 w-3 mr-1" />
                Clear all
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <CustomSelect
                value={filters.status}
                onChange={(val) => setFilters((prev) => ({ ...prev, status: val }))}
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
                placeholder="All Status"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
              <CustomSelect
                value={filters.role}
                onChange={(val) => setFilters((prev) => ({ ...prev, role: val }))}
                options={[
                  { value: '', label: 'All Roles' },
                  { value: ROLES.SUPERADMIN, label: 'Super Admin' },
                  { value: ROLES.DOCTOR, label: 'Doctor' },
                ]}
                placeholder="All Roles"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Created From</label>
              <input
                type="date"
                value={filters.createdFrom}
                onChange={(e) => setFilters((prev) => ({ ...prev, createdFrom: e.target.value }))}
                className="input-field w-full text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Created To</label>
              <input
                type="date"
                value={filters.createdTo}
                onChange={(e) => setFilters((prev) => ({ ...prev, createdTo: e.target.value }))}
                className="input-field w-full text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="card text-center py-12">
          <Loader className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading users...</h3>
          <p className="text-gray-600">Please wait while we fetch the user list.</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && users.length === 0 && (
        <div className="card text-center py-12">
          <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No users yet</h3>
          <p className="text-gray-600 mb-4">Get started by creating your first user account.</p>
          <button onClick={openCreateModal} className="btn-primary inline-flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Add User</span>
          </button>
        </div>
      )}

      {/* No Search Results */}
      {!isLoading && users.length > 0 && filteredUsers.length === 0 && (
        <div className="card text-center py-12">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
          <p className="text-gray-600">
            {hasActiveFilters ? 'Try adjusting your filters or search criteria.' : 'Try adjusting your search criteria.'}
          </p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="mt-4 text-sm text-primary-600 hover:text-primary-700">
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* User Table */}
      {!isLoading && filteredUsers.length > 0 && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Email
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Role
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Created
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <UserCheck className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-4 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{user.name}</div>
                          <div className="text-xs text-gray-500 sm:hidden truncate">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{user.email}</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => openStatusModal(user)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                          user.active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                        title={`Click to ${user.active ? 'deactivate' : 'activate'} user`}
                      >
                        {user.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="text-primary-600 hover:text-primary-900 transition-colors duration-200"
                          title="Edit user"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setUserToDelete(user);
                            setIsDeleteModalOpen(true);
                          }}
                          className="text-red-600 hover:text-red-900 transition-colors duration-200"
                          title="Delete user"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 sm:px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-sm text-gray-600">
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{' '}
                {Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} of{' '}
                {filteredUsers.length} users
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-primary-600 text-white'
                        : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create User Modal */}
      <Modal
        title="Create New User"
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setFormErrors({});
        }}
        widthClassName="max-w-md"
      >
        <form onSubmit={handleCreateUser} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`input-field w-full ${formErrors.name ? 'border-red-500' : ''}`}
              placeholder="Enter full name"
            />
            {formErrors.name && (
              <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`input-field w-full ${formErrors.email ? 'border-red-500' : ''}`}
              placeholder="Enter email address"
            />
            {formErrors.email && (
              <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`input-field w-full ${formErrors.password ? 'border-red-500' : ''}`}
              placeholder="Enter password"
            />
            {formErrors.password && (
              <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special character
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <CustomSelect
              name="role"
              value={formData.role}
              onChange={(val) => {
                setFormData((prev) => ({ ...prev, role: val }));
                if (formErrors.role) setFormErrors((prev) => ({ ...prev, role: '' }));
              }}
              options={[
                { value: ROLES.DOCTOR, label: 'Doctor' },
                { value: ROLES.SUPERADMIN, label: 'Super Admin' },
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <CustomSelect
              name="active"
              value={formData.active}
              onChange={(val) => {
                setFormData((prev) => ({ ...prev, active: val }));
                if (formErrors.active) setFormErrors((prev) => ({ ...prev, active: '' }));
              }}
              options={[
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
              ]}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                setIsCreateModalOpen(false);
                setFormErrors({});
              }}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setUserToEdit(null);
          setEditFormErrors({});
        }}
        widthClassName="max-w-md"
      >
        <form onSubmit={handleEditUser} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={editFormData.name}
              onChange={handleEditInputChange}
              className={`input-field w-full ${editFormErrors.name ? 'border-red-500' : ''}`}
              placeholder="Enter full name"
            />
            {editFormErrors.name && (
              <p className="text-red-500 text-xs mt-1">{editFormErrors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={editFormData.email}
              onChange={handleEditInputChange}
              className={`input-field w-full ${editFormErrors.email ? 'border-red-500' : ''}`}
              placeholder="Enter email address"
            />
            {editFormErrors.email && (
              <p className="text-red-500 text-xs mt-1">{editFormErrors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <CustomSelect
              name="role"
              value={editFormData.role}
              onChange={(val) => {
                setEditFormData((prev) => ({ ...prev, role: val }));
                if (editFormErrors.role) setEditFormErrors((prev) => ({ ...prev, role: '' }));
              }}
              options={[
                { value: ROLES.DOCTOR, label: 'Doctor' },
                { value: ROLES.SUPERADMIN, label: 'Super Admin' },
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <CustomSelect
              name="active"
              value={editFormData.active}
              onChange={(val) => {
                setEditFormData((prev) => ({ ...prev, active: val }));
                if (editFormErrors.active) setEditFormErrors((prev) => ({ ...prev, active: '' }));
              }}
              options={[
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
              ]}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                setIsEditModalOpen(false);
                setUserToEdit(null);
                setEditFormErrors({});
              }}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Status Update Confirmation Modal */}
      <Modal
        title="Update User Status"
        isOpen={isStatusModalOpen}
        onClose={() => {
          setIsStatusModalOpen(false);
          setUserToToggle(null);
        }}
        widthClassName="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to{' '}
            <span className="font-medium">{userToToggle?.active ? 'deactivate' : 'activate'}</span>{' '}
            user <span className="font-medium">{userToToggle?.name}</span>?
          </p>
          <p className="text-sm text-gray-500">
            {userToToggle?.active
              ? 'Deactivating this user will prevent them from logging in.'
              : 'Activating this user will allow them to log in.'}
          </p>
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={() => {
                setIsStatusModalOpen(false);
                setUserToToggle(null);
              }}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleToggleStatus}
              className={`btn-primary ${userToToggle?.active ? 'bg-danger-500 hover:bg-danger-600' : 'bg-green-600 hover:bg-green-700'}`}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Updating...'
                : userToToggle?.active
                ? 'Deactivate'
                : 'Activate'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete User"
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setUserToDelete(null);
        }}
        widthClassName="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete{' '}
            <span className="font-medium">{userToDelete?.name}</span>?
          </p>
          <p className="text-sm text-gray-500">
            This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={() => {
                setIsDeleteModalOpen(false);
                setUserToDelete(null);
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="btn-danger"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Users;
